<template>
    <div class="w-full" :class="{ 'pb-2': !isDashboard }">
      <PageLoader v-if="connectToGafLoader" title="Loading..." message="Please wait." />
      <div class="flex flex-col md:flex-row md:items-center md:justify-between py-2 gap-2" :class="[isDashboard ? 'pl-3' : 'pl-3', !hideDivider ? 'border-b md:border-l border-[#D1D1D1]' : 'border-0']">
        <div class="flex items-center justify-between order-1 md:order-2 w-full md:w-auto">
          <div class="flex items-center gap-4 md:hidden">
            <button class="focus:outline-none cursor-pointer md:hidden" @click="globalStore.toggleLeftNav">
              <i id="global-catalog-menu-icon" class="ri-menu-line text-2xl text-[#6E6E6E]"></i>
            </button>
            <a href="/dashboard" class="flex h-full items-center">
              <img :src="`${MEDIA_ASSET_BASE_URL}${MEDIA_ASSET_LANDING_PATH}LogoHeader.svg`" alt="Roof Hub Logo"
                class="h-6 cursor-pointer" />
            </a>
          </div>
          <div class="flex items-center gap-2">
            <NotificationCenterDropdown v-if="showUnifiedNotificationCenter" />

            <!-- Create New Button -->
            <div class="dropdown relative">
              <BaseButton size="custom" id="global-catalog-create-button" @click.stop="onNewAction()" 
              class="h-10 px-3 !rounded text-base !font-medium">
                <i class="ri-add-box-line text-xl"></i>
                <span class="hidden md:inline">{{ t('globalCatalog.createNewButton') }}</span>
                <i class="ri-arrow-down-s-line hidden md:inline text-lg"></i>
              </BaseButton>
              <CreateNewButtonMenu v-if="showMenu" @close="handleClose" @open-popup="openPopup" />
            </div>
          </div>
        </div>
        <div class="relative w-full md:w-[623px] order-2 md:order-1">
          <SearchProductContainer v-if="enableGlobalQuickSearch" ref="quickSearchRef" access-type="globalSearchBar"
            :placeholder="t('globalCatalog.searchPlacholder')" :product-results="quickSearch.searchResults.value"
            :count="quickSearch.productCount.value" :is-loading="quickSearch.isLoading.value" :enable-keyboard-nav="true"
            :show-browse-catalog-cta="true" :empty-message="t('globalCatalog.quickSearch.emptyMessage')"
            :browse-catalog-label="t('globalCatalog.quickSearch.browseCatalog')" :retain-search-on-select="true"
            @on-search-text="onQuickSearchText" @selected-product="onQuickSearchProductSelected"
            @submit-search="onQuickSearchSubmit" @browse-catalog="onBrowseCatalog" />

          <template v-else>
            <input id="global-catalog-search-input" name="product-search" autocomplete="off" v-model="globalCatalogStore.productSearchTerm"
              @keydown.enter="searchProducts" type="search" :placeholder="t('globalCatalog.searchPlacholder')" class="pl-3 pr-16 h-10 border !border-[#D1D1D1] rounded-lg w-full focus:outline-none text-[#8B8B8B] placeholder:font-normal font-normal text-normal" />
            <i v-if="globalCatalogStore.productSearchTerm" id="global-catalog-clear-search"
              class="ri-close-line absolute right-10 top-1/2 -translate-y-1/2 text-[#000] text-xl md:text-2xl cursor-pointer"
              @click="clearSearch"></i>

            <i id="global-catalog-search-icon"
              class="ri-search-line absolute right-3 top-1/2 -translate-y-1/2 text-[#000] text-xl md:text-2xl cursor-pointer"
              @click="searchProducts"></i>
          </template>
      </div>
    </div>
  </div>
  <!-- Unsaved work confirmation before leaving for the product details page -->
  <Modal :options="{ showCloseButton: false }" :showModal="showLeaveConfirm" @close="cancelLeave">
    <template #header>{{ t('globalCatalog.quickSearch.leavePageTitle') }}</template>
    <template #content>
      <p class="py-3 max-w-[420px]">{{ t('globalCatalog.quickSearch.leavePageMessage') }}</p>
    </template>
    <template #footer>
      <div class="flex justify-end gap-2 pt-2">
        <button type="button" id="global-quick-search-stay-button" @click="cancelLeave"
          class="h-10 px-3 rounded border border-[#D1D1D1] text-base font-medium">
          {{ t('globalCatalog.quickSearch.leavePageCancel') }}
        </button>
        <button type="button" id="global-quick-search-continue-button" @click="confirmLeave"
          class="h-10 px-3 rounded bg-[#762123] text-white text-base font-medium">
          {{ t('globalCatalog.quickSearch.leavePageConfirm') }}
        </button>
      </div>
    </template>
  </Modal>
  <!-- Dynamically render the popup -->
   <Modal :options="options" :showModal="isModalVisible" @close="isModalVisible = false">
  
      <template #content>
        <component
          v-if="activePopup"
          :is="popupComponents[activePopup]"
          @close="closePopup"
          :redirectRoute="activePopupRoute"
        />
      </template>
    </Modal>
  
</template>

<script setup>
import { onMounted, ref, watch, computed, inject } from 'vue';
import { useI18n } from 'vue-i18n'
import { getLanguage } from '@/utilities/cookies';
import { useGlobalStore } from '@/stores/globalStore'
import { MEDIA_ASSET_BASE_URL, MEDIA_ASSET_LANDING_PATH } from "@/config/constants.js";
import { useGlobalCatalogStore } from '@/stores/globalCatalogStore'
import CreateNewButtonMenu from '@/components/CreateNewButtonMenu.vue';
import QuickMeasure from '../QuickMeasurePopup.vue';
import Modal from '@/components/common/Modal.vue';
import HoverIntegrationDialog from '../HoverIntegrationDialog.vue';
import WeatherAlertsDropdown from '@/components/WeatherAlertsDropdown.vue';
import NotificationCenterDropdown from '@/components/NotificationCenterDropdown.vue';
import { useGafStore } from '@/stores/gafStore';
import { useHoverStore } from '@/stores/hoverStore';
import PageLoader from '@/components/common/PageLoader.vue';
import SearchProductContainer from '@/components/OrderTemplate/SearchProductContainer.vue';
import { useProductQuickSearch } from '@/composables/useProductQuickSearch';
import { useDebounce } from '@/utilities/debounce';
import BaseButton from '@/components/atoms/buttons/BaseButton.vue';
const QUICK_SEARCH_PRODUCT_KEY = 'globalQuickSearchProductId';
const GLOBAL_CATALOG_PATH = '/products';


const gafStore = useGafStore();
const hoverStore = useHoverStore();

const { t, locale } = useI18n()
locale.value = getLanguage();

const globalStore = useGlobalStore();
const globalCatalogStore = useGlobalCatalogStore();

// Inject config for feature flags
const config = inject('config');
const enableNotificationCenter = computed(() => config?.value?.enableNotificationCenter ?? false);
const showOrderStatusNotification = computed(() => config?.value?.showOrderStatusNotification ?? false);
const enableWeatherAlerts = computed(() => config?.value?.enableWeatherAlertsFeature ?? false);
const showUnifiedNotificationCenter = computed(() => enableNotificationCenter.value || showOrderStatusNotification.value || enableWeatherAlerts.value);

const searchProducts = () => {
  if (globalCatalogStore.productSearchTerm.length >= 3) {
    if ( window.location.pathname !== "/products" ) {
      localStorage.setItem('productSearchTerm', globalCatalogStore.productSearchTerm);
      window.location = "/products"
    } else {
      localStorage.setItem('productSearchTerm', globalCatalogStore.productSearchTerm);
      globalCatalogStore.loadProducts();
    }
  }
}

const clearSearch = () => globalCatalogStore.clearProductSearch();

// ----- Global quick search (feature flagged) -----
// The global catalog page has its own search, filters and result grid, so the suggestion
// dropdown is suppressed there and the plain input drives globalCatalogStore.loadProducts().
const isGlobalCatalogPage = window.location.pathname === GLOBAL_CATALOG_PATH;
const enableGlobalQuickSearch = computed(() => (config?.value?.enableGlobalQuickSearch ?? false) && !isGlobalCatalogPage);
const quickSearchRef = ref(null);
const quickSearch = useProductQuickSearch();
const showLeaveConfirm = ref(false);
const pendingProduct = ref(null);

// Same debounce interval as the estimate / order template search.
const debouncedQuickSearch = useDebounce(() => quickSearch.getSuggestions({
  branchCode: globalCatalogStore.selectedBranchCode || ''
}), 1000);

function onQuickSearchText(term) {
  globalCatalogStore.productSearchTerm = term ?? '';
  quickSearch.searchTerm.value = term ?? '';
  if (!term || term.length < 3) {
    quickSearch.reset();
    return;
  }
  debouncedQuickSearch();
}

function onQuickSearchSubmit(term) {
  globalCatalogStore.productSearchTerm = term ?? '';
  searchProducts();
}

function onBrowseCatalog() {
  if (window.location.pathname !== '/products') {
    window.location = '/products';
  }
}

/** True when the user has BOM items in progress that would be lost on navigation. */
const hasUnsavedWork = computed(() => (globalCatalogStore.externalCartForBOM?.length ?? 0) > 0);

function onQuickSearchProductSelected({ product }) {
  if (!product) return;
  if (hasUnsavedWork.value) {
    pendingProduct.value = product;
    showLeaveConfirm.value = true;
    return;
  }
  goToProductDetails(product);
}

function cancelLeave() {
  showLeaveConfirm.value = false;
  pendingProduct.value = null;
}

function confirmLeave() {
  showLeaveConfirm.value = false;
  const product = pendingProduct.value;
  pendingProduct.value = null;
  goToProductDetails(product);
}

function goToProductDetails(product) {
  const productId = product?.productId ?? product?.ProductId;
  if (!productId) return;
  if (window.location.pathname === '/products') {
    globalCatalogStore.setSelectedProduct(productId, product);
    return;
  }
  // The product detail view lives inside the /products app, so hand the id over on navigation.
  localStorage.setItem(QUICK_SEARCH_PRODUCT_KEY, String(productId));
  window.location = '/products';
}

const showMenu = ref(false)
function onNewAction() {
  showMenu.value = !showMenu.value
}
const activePopup = ref(null)
const popupComponents = {
  QuickMeasure: QuickMeasure,
  HoverIntegration: HoverIntegrationDialog,
}

const isModalVisible = ref(false)
const activePopupRoute = ref(null)
const connectToGafLoader = ref(false);

watch(
  () => globalCatalogStore.integrationPopupItem,
  item => {
    if ( item ) {
      openPopup(item.popup, item.route)
    } else {
      closePopup()
    }
  }
)

async function openPopup(popupName, route = null) {
  activePopupRoute.value = route;
  showMenu.value = false;
  if (popupName === 'QuickMeasure') {
    connectToGafLoader.value = true;
    await gafStore.connectToGaf();
    if (gafStore.accountExists && gafStore.isConfigured) {
      window.location.href = route;
    } else {
      connectToGafLoader.value = false;
      activePopup.value = popupName;
      isModalVisible.value = true;
    }
    return;
  }

  //  HOVER 
  if (popupName === 'HoverIntegration') {
    if (!hoverStore.loading && hoverStore.isHoverConfigured) {
      window.location.href = route;
      return;
    }
    
    activePopup.value = popupName;
    isModalVisible.value = true;
    return;
  }

  activePopup.value = popupName;
  isModalVisible.value = true;
}

function closePopup() {
  activePopup.value = null
  isModalVisible.value = false
}

function handleClose() {
  showMenu.value = false;
}

const options = {
  showCloseButton: false
}

const hideDivider = window.location.pathname === '/products'
const isDashboard = window.location.pathname === '/dashboard'

onMounted(async () => {
  hoverStore.connectToHover();
  // Only read and use the productSearchTerm from localStorage when on the /products page.
  // This prevents automatic redirects when navigating to other pages like the dashboard.
  if (window.location.pathname === '/products') {
    const quickSearchProductId = localStorage.getItem(QUICK_SEARCH_PRODUCT_KEY);
    if (quickSearchProductId) {
      localStorage.removeItem(QUICK_SEARCH_PRODUCT_KEY);
      try {
        await globalCatalogStore.branchesReady;
      } catch (e) {
        // Branch resolution failed; open the product with whatever branch context exists.
      }
      globalCatalogStore.setSelectedProduct(quickSearchProductId);
      return;
    }
    const productSearchTerm = localStorage.getItem("productSearchTerm");
    if ( productSearchTerm ) {
      globalCatalogStore.productSearchTerm = productSearchTerm;
      // Wait for the eligible branch to resolve before searching. Otherwise the first
      // request runs unfiltered and a second, branch-filtered request is auto-triggered
      // once the branch loads - which wiped the results (bug #106015).
      try {
        await globalCatalogStore.branchesReady;
      } catch (e) {
        // Branch resolution failed; search anyway with whatever branch context exists.
      }
      globalCatalogStore.loadProducts();
      localStorage.removeItem('productSearchTerm');
    }
  } else {
    // Clear any stale search markers on other pages to prevent future unintended redirects
    localStorage.removeItem('productSearchTerm');
  }
})
</script>